import { useEffect, useRef } from 'react';

// Minimum gap between cross-tab last-activity writes to localStorage.
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;

/**
 * Automatic logout after a period of user inactivity.
 *
 * - Listens to mouse / keyboard / touch / scroll events to detect activity.
 * - Shows a warning toast `warnBeforeMs` before logging out (default 60s).
 * - Cross-tab aware: stores the last-activity timestamp in localStorage so
 *   activity in one tab counts for all tabs of the same user.
 *
 * Mount it once near the top of the tree (e.g. inside <App />) and only when
 * the user is authenticated.
 *
 * @example
 *   useInactivityLogout({
 *     enabled: !!user,
 *     timeoutMs: 30 * 60 * 1000,
 *     onTimeout: () => { void logout(); },
 *   });
 */
export function useInactivityLogout({
  enabled,
  timeoutMs,
  warnBeforeMs = 60_000,
  onTimeout,
  onWarn,
  storageKey = 'forxa-last-activity',
}: {
  enabled: boolean;
  timeoutMs: number;
  warnBeforeMs?: number;
  onTimeout: () => void | Promise<void>;
  onWarn?: (msRemaining: number) => void;
  storageKey?: string;
}) {
  const warnedRef = useRef(false);
  const timersRef = useRef<{ warn?: number; logout?: number }>({});
  // Keep the latest callbacks in refs so inline/unstable callbacks from the
  // caller never tear down and re-register the activity listeners.
  const onTimeoutRef = useRef(onTimeout);
  const onWarnRef = useRef(onWarn);
  onTimeoutRef.current = onTimeout;
  onWarnRef.current = onWarn;

  useEffect(() => {
    if (!enabled || timeoutMs <= 0) return;

    function clearTimers() {
      if (timersRef.current.warn) window.clearTimeout(timersRef.current.warn);
      if (timersRef.current.logout) window.clearTimeout(timersRef.current.logout);
      timersRef.current = {};
    }

    function scheduleFromLastActivity(lastActivityAt: number) {
      clearTimers();
      const now = Date.now();
      const elapsed = now - lastActivityAt;
      const remaining = timeoutMs - elapsed;

      if (remaining <= 0) {
        void onTimeoutRef.current();
        return;
      }

      const warnAt = Math.max(0, remaining - warnBeforeMs);
      timersRef.current.warn = window.setTimeout(() => {
        if (!warnedRef.current) {
          warnedRef.current = true;
          onWarnRef.current?.(warnBeforeMs);
        }
      }, warnAt);

      timersRef.current.logout = window.setTimeout(() => {
        void onTimeoutRef.current();
      }, remaining);
    }

    // Debounce handle — lives in the effect closure, cleared on cleanup.
    let rescheduleTimer = 0;
    // Cross-tab timestamp writes are throttled: activity events (scroll,
    // typing) can fire dozens of times per second and every localStorage
    // write is synchronous and fans out a `storage` event to all other tabs.
    let lastStorageWriteAt = 0;
    let pendingStorageWrite = false;
    let latestActivityAt = 0;

    function writeActivityTimestamp(at: number) {
      lastStorageWriteAt = at;
      pendingStorageWrite = false;
      try {
        localStorage.setItem(storageKey, String(at));
      } catch {
        // localStorage may throw in private mode; ignore.
      }
    }

    function recordActivity() {
      warnedRef.current = false;
      const now = Date.now();
      latestActivityAt = now;
      // Leading-edge write at most once per ACTIVITY_WRITE_THROTTLE_MS so
      // other tabs learn about activity quickly without a write per event.
      if (now - lastStorageWriteAt >= ACTIVITY_WRITE_THROTTLE_MS) {
        writeActivityTimestamp(now);
      } else {
        pendingStorageWrite = true;
      }
      // Collapse rapid bursts of events (typing, scrolling) into a single
      // timer reschedule 1.5 s after the last event instead of recreating
      // timers on every keystroke. The trailing write keeps the shared
      // timestamp exact once the burst ends.
      window.clearTimeout(rescheduleTimer);
      rescheduleTimer = window.setTimeout(() => {
        if (pendingStorageWrite) writeActivityTimestamp(latestActivityAt);
        scheduleFromLastActivity(Date.now());
      }, 1500);
    }

    function readLastActivity(): number {
      try {
        const stored = localStorage.getItem(storageKey);
        const parsed = stored ? Number(stored) : NaN;
        return Number.isFinite(parsed) ? parsed : Date.now();
      } catch {
        return Date.now();
      }
    }

    function handleStorage(event: StorageEvent) {
      if (event.key !== storageKey || !event.newValue) return;
      const t = Number(event.newValue);
      if (Number.isFinite(t)) scheduleFromLastActivity(t);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        scheduleFromLastActivity(readLastActivity());
      }
    }

    // Activity events — passive to avoid scroll-jank
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'pointerdown'] as const;
    for (const evt of activityEvents) {
      window.addEventListener(evt, recordActivity, { passive: true });
    }
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // On fresh enable (login), always stamp "now" so we never inherit a stale
    // timestamp from a previous session and fire the timeout immediately.
    const storedRaw = (() => {
      try { return localStorage.getItem(storageKey); } catch { return null; }
    })();
    const storedMs = storedRaw ? Number(storedRaw) : NaN;
    const isStale = !Number.isFinite(storedMs) || (Date.now() - storedMs) >= timeoutMs;
    if (isStale) {
      try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* ignore */ }
    }

    // Initial schedule, anchored to whatever the most recent activity was
    scheduleFromLastActivity(readLastActivity());

    return () => {
      for (const evt of activityEvents) {
        window.removeEventListener(evt, recordActivity);
      }
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimers();
      window.clearTimeout(rescheduleTimer);
    };
  }, [enabled, timeoutMs, warnBeforeMs, storageKey]);
}
